import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { 
  Shield, 
  AlertTriangle, 
  Brain,
  FileWarning,
  Database,
  Zap,
  Filter,
  Lock,
  Activity,
  Eye,
} from 'lucide-react';
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { cn } from '../../utils/cn'
import { formatRelativeTime } from '../../utils/format'
import { NOT_MEASURED, NO_DATA_SOURCE, NotMeasuredNotice } from './NotMeasured'
import type { SocketCloudAiSecurityTypes } from '@aaimplatform/socket-cloud-types';
// AI-specific security types
type AISecurityEvent = SocketCloudAiSecurityTypes.components['schemas']['AISecurityEvent'];

type PromptAnalysis = SocketCloudAiSecurityTypes.components['schemas']['PromptAnalysis'];

// No data source feeds this page yet (GH #3969). The lists stay empty and every
// metric reads NOT_MEASURED until the egress gateway's pass-through log is wired
// (GH #3958); the page invents no event, analysis, count or model name.
const NO_SECURITY_EVENTS: AISecurityEvent[] = [];
const NO_PROMPT_ANALYSES: PromptAnalysis[] = [];

const METRIC_TILES = [
  'Prompts Analyzed',
  'Threats Detected',
  'Threats Blocked',
  'Avg Latency',
  'False Positive Rate',
  'Models Protected',
  'Tools Monitored',
] as const;

const THREAT_PATTERNS = [
  'Prompt Injections',
  'Data Extraction',
  'Jailbreak Attempts',
  'Resource Attacks',
] as const;

export const MCPSecurityGateway: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [viewMode, setViewMode] = useState<'flows' | 'events' | 'analysis'>('flows');
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('1h');
  
  const securityEvents = NO_SECURITY_EVENTS;
  const promptAnalyses = NO_PROMPT_ANALYSES;

  // Data flow visualization
  useEffect(() => {
    if (!svgRef.current || viewMode !== 'flows') {
return;
}

    const container = svgRef.current.parentElement;
    if (!container) {
return;
}

    // Calculate responsive dimensions to fit container
    const containerRect = container.getBoundingClientRect();
    const width = Math.max(containerRect.width - 32, 900); // Min width to prevent clipping
    const height = Math.max(Math.min(containerRect.height - 100, 500), 350); // Min height of 350
    const margin = { top: 40, right: 40, bottom: 40, left: 40 }; // Increased margins for better spacing

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    svg.selectAll('*').remove();

    // Define SVG filters for premium effects
    const defs = svg.append('defs');
    
    // Gaussian blur filter
    const blurFilter = defs.append('filter')
      .attr('id', 'blur-filter')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    blurFilter.append('feGaussianBlur')
      .attr('in', 'SourceGraphic')
      .attr('stdDeviation', '3');
    
    // Glow filter
    const glowFilter = defs.append('filter')
      .attr('id', 'glow-filter')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    glowFilter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur');
    const glowMerge = glowFilter.append('feMerge');
    glowMerge.append('feMergeNode').attr('in', 'coloredBlur');
    glowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Add subtle background gradient
    const bgGradient = defs.append('linearGradient')
      .attr('id', 'bg-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
    
    bgGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#1e293b')
      .attr('stop-opacity', 0.5);
    bgGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#0f172a')
      .attr('stop-opacity', 0.8);
    
    // Background rect
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'url(#bg-gradient)');

    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const effectiveWidth = Math.max(width - margin.left - margin.right, 100);
    const effectiveHeight = Math.max(height - margin.top - margin.bottom, 100);

    // Define 7 zones with sophisticated color palette
    const totalZoneUnits = 7.5; // Reduced gateway width from 2 to 1.5
    const zoneWidth = effectiveWidth / totalZoneUnits;
    const zones = [
      { id: 'external-models', label: 'External Models', x: 0, width: zoneWidth, color: '#8b5cf6', baseColor: 'rgb(139, 92, 246)', trust: 'untrusted' },
      { id: 'external-tools', label: 'External Tools', x: zoneWidth, width: zoneWidth, color: '#a855f7', baseColor: 'rgb(168, 85, 247)', trust: 'untrusted' },
      { id: 'gateway', label: 'SocketCloud™ Secure Framework', x: zoneWidth * 2, width: zoneWidth * 1.5, color: '#0ea5e9', baseColor: 'rgb(14, 165, 233)', trust: 'security' },
      { id: 'internal-models', label: 'Internal Models', x: zoneWidth * 3.5, width: zoneWidth, color: '#06b6d4', baseColor: 'rgb(6, 182, 212)', trust: 'trusted' },
      { id: 'internal-tools', label: 'Internal Tools', x: zoneWidth * 4.5, width: zoneWidth, color: '#14b8a6', baseColor: 'rgb(20, 184, 166)', trust: 'trusted' },
      { id: 'systems', label: 'Systems', x: zoneWidth * 5.5, width: zoneWidth, color: '#10b981', baseColor: 'rgb(16, 185, 129)', trust: 'trusted' },
      { id: 'users', label: 'Users', x: zoneWidth * 6.5, width: zoneWidth, color: '#34d399', baseColor: 'rgb(52, 211, 153)', trust: 'trusted' },
    ];

    // Create gradient definitions for each zone
    zones.forEach((zone) => {
      const gradient = defs.append('linearGradient')
        .attr('id', `zone-gradient-${zone.id}`)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');
      
      if (zone.trust === 'security') {
        // Gateway gets a special animated gradient
        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.08);
        gradient.append('stop')
          .attr('offset', '50%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.12);
        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.08);
      } else {
        // Other zones get subtle gradients
        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.02);
        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.06);
      }
    });

    // Draw zones with sophisticated styling
    zones.forEach((zone) => {
      const zoneGroup = g.append('g');
      
      // Zone background with gradient
      zoneGroup.append('rect')
        .attr('x', zone.x)
        .attr('y', 0)
        .attr('width', Math.max(zone.width, 1))
        .attr('height', Math.max(effectiveHeight, 1))
        .attr('fill', `url(#zone-gradient-${zone.id})`)
        .attr('rx', 0);
      
      // Zone label with refined typography
      const labelY = -15;
      
      if (zone.trust === 'security') {
        // Special treatment for gateway label - use SocketCloud logo
        const logoGroup = zoneGroup.append('g')
          .attr('transform', `translate(${zone.x + zone.width / 2}, ${labelY})`);
        
        // Create logo image - adjusted vertical position to align with text baseline
        logoGroup.append('image')
          .attr('href', '/socketcloud-logo.svg')
          .attr('width', 120) // Slightly smaller for better proportion
          .attr('height', 18)  // Proportional height
          .attr('x', -60)     // Center horizontally
          .attr('y', -14)     // Adjusted to align baseline with text headers
          .style('opacity', 0.9)
          .style('filter', 'brightness(1.1)'); // Slightly brighten for better visibility
      } else {
        // Regular zone labels
        zoneGroup.append('text')
          .attr('x', zone.x + zone.width / 2)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('fill', zone.color)
          .attr('fill-opacity', 0.9)
          .attr('font-size', '12px')
          .attr('font-weight', '500')
          .attr('letter-spacing', '0.3px')
          .text(zone.label);
      }
      
      // Subtle trust indicators at bottom
      const indicatorY = effectiveHeight + 15;
      const indicatorStyle = {
        'font-size': '10px',
        'font-weight': '400',
        'letter-spacing': '1px',
        'text-transform': 'uppercase',
      };
      
      if (zone.trust === 'untrusted') {
        const indicator = zoneGroup.append('text')
          .attr('x', zone.x + zone.width / 2)
          .attr('y', indicatorY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#a855f7')
          .attr('fill-opacity', 0.6);
        
        Object.entries(indicatorStyle).forEach(([key, value]) => {
          indicator.style(key, value);
        });
        indicator.text('External');
      } else if (zone.trust === 'trusted') {
        const indicator = zoneGroup.append('text')
          .attr('x', zone.x + zone.width / 2)
          .attr('y', indicatorY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#10b981')
          .attr('fill-opacity', 0.6);
        
        Object.entries(indicatorStyle).forEach(([key, value]) => {
          indicator.style(key, value);
        });
        indicator.text('Internal');
      } else if (zone.trust === 'security') {
        const indicator = zoneGroup.append('text')
          .attr('x', zone.x + zone.width / 2)
          .attr('y', indicatorY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#0ea5e9')
          .attr('fill-opacity', 0.6);
        
        Object.entries(indicatorStyle).forEach(([key, value]) => {
          indicator.style(key, value);
        });
        indicator.text('Secure Framework');
      }
    });

    // Add entities to zones
    const entities = {
      // No source reports which providers the gateway reaches (GH #3969), so
      // the zone names none.
      'external-models': [
        { name: 'Model providers', icon: '', y: 0.5 },
      ],
      'external-tools': [
        { name: 'Web Search', icon: '🔍', y: 0.3 },
        { name: 'External APIs', icon: '🌐', y: 0.5 },
        { name: '3rd Party MCP', icon: '🔌', y: 0.7 },
      ],
      'gateway': [
        { name: 'Rate Limiter', icon: '🚦', y: 0.2 },
        { name: 'Threat Scanner', icon: '🛡️', y: 0.4 },
        { name: 'Content Filter', icon: '🔍', y: 0.6 },
        { name: 'Policy Engine', icon: '📋', y: 0.8 },
      ],
      'internal-models': [
        { name: 'Company LLM', icon: '🏢', y: 0.3 },
        { name: 'Fine-tuned Models', icon: '🎯', y: 0.5 },
        { name: 'Local Models', icon: '💾', y: 0.7 },
      ],
      'internal-tools': [
        { name: 'Database', icon: '🗄️', y: 0.25 },
        { name: 'File System', icon: '📁', y: 0.5 },
        { name: 'Internal APIs', icon: '🔧', y: 0.75 },
      ],
      'systems': [
        { name: 'App Servers', icon: '🖥️', y: 0.3 },
        { name: 'Orchestrator', icon: '🎼', y: 0.5 },
        { name: 'Cache Layer', icon: '⚡', y: 0.7 },
      ],
      'users': [
        { name: 'Developers', icon: '👩‍💻', y: 0.25 },
        { name: 'API Clients', icon: '📱', y: 0.5 },
        { name: 'End Users', icon: '👥', y: 0.75 },
      ],
    };

    // Draw entities with premium pill-shaped design
    const entityHeight = 48; // Slightly smaller for elegance
    const entityStartY = 35; // Start position for all zones
    const entitySpacing = 12; // More breathing room
    
    zones.forEach(zone => {
      const zoneEntities = entities[zone.id as keyof typeof entities] || [];
      zoneEntities.forEach((entity, index) => {
        const entityGroup = g.append('g')
          .attr('class', 'entity-group');
        
        const x = zone.x + zone.width / 2;
        const y = entityStartY + index * (entityHeight + entitySpacing);
        
        // Create gradient for each entity
        const entityGradient = defs.append('radialGradient')
          .attr('id', `entity-gradient-${zone.id}-${index}`)
          .attr('cx', '50%')
          .attr('cy', '50%')
          .attr('r', '50%');
        
        entityGradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.15);
        entityGradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', zone.baseColor)
          .attr('stop-opacity', 0.05);
        
        // Entity pill background
        entityGroup.append('rect')
          .attr('x', x - 45)
          .attr('y', y - 22)
          .attr('width', 90)
          .attr('height', 44)
          .attr('fill', `url(#entity-gradient-${zone.id}-${index})`)
          .attr('rx', 22);
        
        // Entity icon
        entityGroup.append('text')
          .attr('x', x)
          .attr('y', y - 3)
          .attr('text-anchor', 'middle')
          .attr('font-size', '18px')
          .attr('opacity', 0.9)
          .text(entity.icon);
        
        // Entity label
        entityGroup.append('text')
          .attr('x', x)
          .attr('y', y + 12)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', '400')
          .attr('fill', '#e5e7eb')
          .attr('fill-opacity', 0.8)
          .attr('letter-spacing', '0.2px')
          .text(entity.name);
      });
    });

    // Add sophisticated statistics in the gateway zone
    const gatewayZone = zones.find(z => z.id === 'gateway');
    if (gatewayZone) {
      const statsY = entityStartY + 4 * (entityHeight + entitySpacing) + 25;
      
      // Only show stats if there's enough space
      if (statsY + 70 < effectiveHeight) {
        const statsGroup = g.append('g');
        
        // Create gradient for stats background
        const statsGradient = defs.append('linearGradient')
          .attr('id', 'stats-gradient')
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '100%')
          .attr('y2', '100%');
        
        statsGradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', '#0ea5e9')
          .attr('stop-opacity', 0.05);
        statsGradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', '#06b6d4')
          .attr('stop-opacity', 0.1);
        
        // Stats background with premium styling
        statsGroup.append('rect')
          .attr('x', gatewayZone.x + 20)
          .attr('y', statsY)
          .attr('width', Math.max(gatewayZone.width - 40, 1))
          .attr('height', 65)
          .attr('fill', 'url(#stats-gradient)')
          .attr('rx', 12)
          .attr('filter', 'url(#blur-filter)');
        
        // Requests, blocks and the protected share are not measured until a
        // data source is wired (GH #3969); the panel says so instead of
        // showing numbers.
        statsGroup.append('text')
          .attr('x', gatewayZone.x + gatewayZone.width / 2)
          .attr('y', statsY + 37)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('font-weight', '500')
          .attr('fill', '#94a3b8')
          .attr('letter-spacing', '0.5px')
          .text(NOT_MEASURED);
      }
    }

  }, [viewMode]);

  const getEventIcon = (type: AISecurityEvent['type']) => {
    const icons = {
      prompt_injection: Zap,
      data_extraction: Database,
      jailbreak: Lock,
      hallucination: Brain,
      sensitive_output: FileWarning,
      resource_exhaustion: Activity,
      unauthorized_tool: Shield,
    };
    return icons[type as keyof typeof icons] || AlertTriangle;
  };

  const getSeverityColor = (severity: string) => {
    const colors = {
      critical: 'text-red-500',
      high: 'text-orange-500',
      medium: 'text-yellow-500',
      low: 'text-primary-500',
    };
    return colors[severity as keyof typeof colors] || 'text-gray-500';
  };

  return (
    <div className="space-y-4">
      {/* Header and Metrics */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">AI Security Gateway</h3>
            <p className="text-sm text-gray-400">
              AI threat detection and prevention. No data source is connected, so nothing on this page is measured.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{NO_DATA_SOURCE}</Badge>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as typeof timeRange)}
              className="bg-gray-800 text-gray-300 text-sm rounded px-3 py-1 border border-gray-700 focus:border-primary-500 focus:outline-none"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
          </div>
        </div>

        {/* Metrics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          {METRIC_TILES.map((label) => (
            <div key={label} className="bg-gray-900 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-500">{NOT_MEASURED}</div>
              <div className="text-xs text-gray-400">{label}</div>
            </div>
          ))}
        </div>

        {/* View Mode Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setViewMode('flows')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              viewMode === 'flows' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700/50',
            )}
          >
            <Activity className="w-4 h-4 inline mr-2" />
            Data Flows
          </button>
          <button
            onClick={() => setViewMode('events')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              viewMode === 'events' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700/50',
            )}
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Security Events
          </button>
          <button
            onClick={() => setViewMode('analysis')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              viewMode === 'analysis' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700/50',
            )}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Prompt Analysis
          </button>
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Visualization (2/3 width) */}
          <div className="lg:col-span-2">
            {viewMode === 'flows' && (
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <svg ref={svgRef} className="w-full min-w-[900px]" />
              </div>
            )}

            {viewMode === 'events' && securityEvents.length === 0 && (
              <NotMeasuredNotice message="No security events are recorded: no data source is connected." />
            )}

            {viewMode === 'events' && securityEvents.length > 0 && (
              <div className="bg-gray-900 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                <div className="space-y-2">
                  {securityEvents.map((event) => {
                    const Icon = getEventIcon(event.type);
                    return (
                      <div
                        key={event.id}
                        className={cn(
                          'p-3 rounded-lg border transition-all',
                          event.blocked 
                            ? 'bg-red-900/20 border-red-800/50' 
                            : 'bg-gray-800 border-gray-700',
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <Icon className={cn('w-5 h-5 mt-0.5', getSeverityColor(event.severity))} />
                            <div>
                              <div className="font-medium text-white">
                                {event.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </div>
                              <div className="text-sm text-gray-400 mt-1">{event.details}</div>
                              <div className="flex items-center gap-4 mt-2 text-xs">
                                <span className="text-gray-500">
                                  {event.source.id} → {event.target.name}
                                </span>
                                <Badge variant={event.severity === 'critical' ? 'danger' : event.severity === 'high' ? 'warning' : 'secondary'}>
                                  {event.severity}
                                </Badge>
                                {event.blocked && (
                                  <Badge variant="danger">Blocked</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatRelativeTime(event.timestamp)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viewMode === 'analysis' && promptAnalyses.length === 0 && (
              <NotMeasuredNotice message="No prompt analyses are recorded: no data source is connected." />
            )}

            {viewMode === 'analysis' && promptAnalyses.length > 0 && (
              <div className="bg-gray-900 rounded-lg p-4 space-y-4">
                {promptAnalyses.map((analysis) => (
                  <div key={analysis.id} className="border border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Badge variant={
                        analysis.action === 'blocked' ? 'danger' : 
                        analysis.action === 'sanitized' ? 'warning' : 
                        'success'
                      }>
                        {analysis.action.toUpperCase()}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {formatRelativeTime(analysis.timestamp)}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Original Prompt:</div>
                        <code className="text-sm text-gray-300 bg-gray-800 p-2 rounded block">
                          {analysis.originalContent}
                        </code>
                      </div>
                      
                      {analysis.action !== 'allowed' && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Processed:</div>
                          <code className="text-sm text-green-400 bg-gray-800 p-2 rounded block">
                            {analysis.processedContent}
                          </code>
                        </div>
                      )}
                      
                      {analysis.threats.length > 0 && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Detected Threats:</div>
                          <div className="space-y-1">
                            {analysis.threats.map((threat, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span className="text-orange-400">{threat.type}</span>
                                <span className="text-gray-400">
                                  {(threat.confidence * 100).toFixed(0)}% confidence
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Side Panel (1/3 width) */}
          <div className="space-y-4">
            {/* Active Threats */}
            <Card className="bg-gray-900 p-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
                Threat Patterns
              </h4>
              <div className="space-y-2">
                {THREAT_PATTERNS.map((pattern) => (
                  <div key={pattern} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{pattern}</span>
                    <span className="text-gray-500">{NOT_MEASURED}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Model Protection Status */}
            <Card className="bg-gray-900 p-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center">
                <Brain className="w-4 h-4 mr-2 text-primary-500" />
                Model Protection Status
              </h4>
              <NotMeasuredNotice message="No models are reported: no data source is connected." />
            </Card>

            {/* Security Policies */}
            <Card className="bg-gray-900 p-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center">
                <Filter className="w-4 h-4 mr-2 text-purple-500" />
                Security Policies
              </h4>
              <NotMeasuredNotice message="No policy is reported as enforced: no data source is connected." />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCPSecurityGateway;